/**
 * @module utils/thumbnail
 *
 * Resolves a stored thumbnail path/URL into an absolute, loadable URL.
 *
 * Stored thumbnails come in three shapes:
 *   - an absolute external URL (archive.org, raiplaysound, youtube, …) → used as-is
 *   - a relative FYT path (e.g. "/public/default-cover.jpg") → resolved against
 *     the API host, so it follows whatever API host the client is pointed at
 *     instead of having a domain baked into the database
 *   - empty / null → falls back to the API's default cover
 *
 * Keeping the host OUT of stored data means a server domain change never breaks
 * old rows — the host is applied fresh at render time from VITE_API_BASE_URL.
 */

// API host the client is configured for (no trailing slash, no "/public").
const API_BASE = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");

// The FYT-hosted default cover, resolved against the current API host.
const DEFAULT_COVER = `${API_BASE}/public/default-cover.jpg`;

/**
 * resolveThumbnail - turn a stored thumbnail value into a loadable URL.
 *
 * @param {string} [path] - stored thumbnail path/URL (absolute, relative, or empty)
 * @returns {string} an absolute URL safe to use as an <img>/v-img src
 */
export function resolveThumbnail(path) {
  // No thumbnail stored → show the default cover from the current API host.
  if (!path) return DEFAULT_COVER;
  // Absolute URLs (http/https), protocol-relative, or data URIs → use as-is.
  if (/^(https?:)?\/\//i.test(path) || path.startsWith("data:")) return path;
  // Relative FYT path → prefix the API host (handles a missing leading slash).
  return `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
}
